import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import type { AuditEntry } from "../lib/types";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/utils";
import { PageHeader } from "../components/core/Layout";
import { DataTable } from "../components/core/DataTable";
import { Input, Select } from "../components/core/Field";
import { TenantSelect } from "../components/shared/TenantSelect";

const ENTITIES = ["tenant", "user", "vehicle", "compliance", "alert", "file", "department", "telematics"];

export default function AuditPage() {
  const { user } = useAuth();
  const isBO = user?.user_type === "super_admin";
  const [params] = useSearchParams();
  const [tenantId, setTenantId] = useState("");
  const [entity, setEntity] = useState(params.get("entity") || "");
  useEffect(() => { setEntity(params.get("entity") || ""); }, [params]);
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery<AuditEntry[]>({ queryKey: ["audit", tenantId, entity, q], queryFn: async () => (await api.get("/audit", { params: { tenant_id: tenantId || undefined, entity_type: entity || undefined, q: q || undefined } })).data });

  return (
    <div data-testid="audit-page">
      <PageHeader eyebrow="Governance" title="Audit Trail" subtitle="Immutable log of every critical action — who, what, when, and for which client."
        actions={<>{isBO && <TenantSelect value={tenantId} onChange={setTenantId} className="w-52" />}</>} />
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input data-testid="audit-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search summary, actor, action…" className="w-72 pl-8" /></div>
        <Select data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)} className="w-44"><option value="">All entities</option>{ENTITIES.map((e) => <option key={e} value={e}>{e}</option>)}</Select>
      </div>
      <DataTable<AuditEntry>
        testId="audit-table" loading={isLoading} rows={data || []} rowKey={(a) => a.id} rowTestId={(a) => `audit-row-${a.id}`}
        columns={[
          { key: "time", header: "Timestamp", render: (a) => <span className="whitespace-nowrap font-mono text-[11px]">{fmtDateTime(a.created_at)}</span> },
          { key: "actor", header: "Actor", render: (a) => <div><p className="text-xs font-semibold text-slate-800">{a.actor_email}</p><p className="text-[10px] uppercase text-slate-400">{a.actor_type?.replace("_", " ")}</p></div> },
          ...(isBO ? [{ key: "tenant", header: "Client", render: (a: AuditEntry) => <span className="text-xs">{a.tenant_name || <span className="text-slate-400">Platform</span>}</span> }] : []),
          { key: "action", header: "Action", render: (a) => <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">{a.action}</span> },
          { key: "summary", header: "Summary", render: (a) => <span className="text-sm">{a.summary}</span> },
          { key: "entity", header: "Entity", render: (a) => <span className="font-mono text-[10px] text-slate-400">{a.entity_type}{a.entity_id ? `:${a.entity_id.slice(-6)}` : ""}</span> },
        ]}
        empty="No audit entries."
      />
    </div>
  );
}
